import Sentry from "@similarweb/sentry-logger";

import { toTimeGranularitySymbol } from "app/common/utils/timeGranularity";
import ColorStack from "app/components/colorsStack/ColorStack";
import { MARKET_SHARES_GRAPH_METRIC_TYPE } from "app/components/dashboard/widget-wizard/components/MarketAnalysis/constants";
import { DASHBOARD_ID_PREVIEW_MODE } from "app/components/dashboard/widget-wizard/constants";
import { selectCurrentDashboardId } from "app/components/dashboard/widget-wizard/selectors/dashboardSelectors";
import MarketAnalysisWidgetFilters from "app/components/widget/widget-filters/MarketAnalysisWidgetFilters";
import MarketAnalysisSubtitle from "app/components/widget/widget-subtitle-templates/MarketAnalysisSubtitle";
import { CHART_COLORS } from "app/constants/ChartColors";
import { selectDashboardSegments } from "app/pages/dashboard/redux/selectors";
import type { IRawData, SwitchGraphCompareData } from "app/pages/industry-analysis/players/market-shares/types";
import { parseRawData } from "app/pages/industry-analysis/players/market-shares/utils/parseRawData";
import {
    CHART_DISPLAY_TYPE_MD,
    chartDisplayTypeFromDataMode,
    dashboardDataDisplayType,
    dataDisplayTypeForUrl,
} from "app/pages/industry-analysis/players/market-shares/utils/utils";
import { loadCustomSegmentsMetadataForDashboard } from "app/pages/segments/config/segmentsConfigHelpers";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";

import { LegendItems } from "../../widget-utilities/segments/LegendItems";
import type { LegendPropsTypes } from "../../widget-utilities/segments/LegendItems";
import { GraphWidget } from "../GraphWidget";
import { WidgetMessages, WidgetState } from "../Widget";

import { getAreaChartConfig } from "./configs";
import {
    createLegendsItems,
    filterGraphResponseBySelectedKeys,
    getApiParams,
    getIndustrieById,
    resolveMarketSharesApiTimeGranularity,
} from "./helpers";

const ENDPOINT = "api/Market/Trends/SwitchGraphV2";
type MarketSharesWidgetFilters = Record<string, unknown> & { timeGranularity?: string };
type MarketSharesDataMode = keyof typeof chartDisplayTypeFromDataMode;

export class MarketSharesGraphWidget extends GraphWidget {
    public metric;
    reactLegendComponent: ({ legendItems, colors }: LegendPropsTypes) => React.ReactNode;

    private get isPreviewMode(): boolean {
        return this.dashboardId === DASHBOARD_ID_PREVIEW_MODE;
    }

    private get resolvedWidgetFilters(): MarketSharesWidgetFilters {
        const state = this._reduxService.getState();
        const persistedFilters = this._widgetConfig.properties.filters as MarketSharesWidgetFilters | undefined;
        const wizardFilters = state.customDashboard.dashboardWizard.widget?.filters as
            | MarketSharesWidgetFilters
            | undefined;

        if (this.isPreviewMode) {
            return wizardFilters ?? persistedFilters ?? {};
        }

        return persistedFilters ?? {};
    }

    private get resolvedDataMode(): MarketSharesDataMode | undefined {
        const state = this._reduxService.getState();
        const persistedDataMode = this._widgetConfig.properties.dataMode as MarketSharesDataMode | undefined;
        const wizardDataMode = state.customDashboard.dashboardWizard.widget?.dataMode as
            | MarketSharesDataMode
            | undefined;

        return this.isPreviewMode ? wizardDataMode || persistedDataMode : persistedDataMode;
    }

    private get resolvedChartDisplayType() {
        const selectedDataMode = this.resolvedDataMode;

        return selectedDataMode ? chartDisplayTypeFromDataMode[selectedDataMode] : this.apiParams.chartDisplayType;
    }

    private get resolvedChartDisplayTypeForUrl() {
        const selectedDataMode = this.resolvedDataMode;
        const dataMode = selectedDataMode ?? dashboardDataDisplayType[this.apiParams.chartDisplayType];

        return dataDisplayTypeForUrl[dataMode];
    }

    constructor() {
        super();
        this.metric = MARKET_SHARES_GRAPH_METRIC_TYPE;
    }

    static getWidgetMetadataType() {
        return MARKET_SHARES_GRAPH_METRIC_TYPE;
    }

    static getWidgetDashboardType() {
        return "Graph";
    }

    static getFiltersComponent() {
        return MarketAnalysisWidgetFilters;
    }

    public getViewData() {
        super.getViewData();
        const { forWidget } = DurationService.getDurationData(
            this._widgetConfig.properties.duration,
            this._widgetConfig.properties.comparedDuration,
            this._metricConfig.component,
        );

        this.viewData.customSubtitle = "true";
        this.viewData.customSubtitleComponent = MarketAnalysisSubtitle;
        this.viewData.duration = forWidget;
        this.viewData.isDashboard = this._swNavigator.isDashboard();

        this.emit(WidgetMessages.WIDGET_UPDATED, this.viewData);
        return this.viewData;
    }

    protected setMetadata() {
        const widgetProp = this._widgetConfig.properties;
        const chartOptions = {
            type: "Graph",
            height: widgetProp.height,
            dashboardId: this.dashboardId,
        };
        this.metadata = { chartOptions };
    }

    public isPptSupported = () => {
        return false;
    };

    getDataMetric = async () => {
        const state = this._reduxService.getState();

        const fetchService = DefaultFetchService.getInstance();
        const filters = this.resolvedWidgetFilters;
        const duration = this.durationObject.forAPI;
        const { webSource } = this.apiParams;

        const apiParams = getApiParams(this.apiParams, duration, filters, MARKET_SHARES_GRAPH_METRIC_TYPE);

        const res: SwitchGraphCompareData = await fetchService.get(ENDPOINT, apiParams);
        const keys = this._widgetConfig.properties.key;

        const dashboardSegments = selectDashboardSegments(state);

        if (!dashboardSegments && this.widgetConfig?.includesSegments) {
            const id =
                this.dashboardId === DASHBOARD_ID_PREVIEW_MODE ? selectCurrentDashboardId(state) : this.dashboardId;

            await loadCustomSegmentsMetadataForDashboard(id);
        }

        const filteredData = filterGraphResponseBySelectedKeys(res.Data, keys);

        return parseRawData(filteredData, keys, webSource);
    };

    initChart(parsedGraphData: IRawData) {
        const chartDisplayType = this.resolvedChartDisplayType;
        const { formatter } = CHART_DISPLAY_TYPE_MD[chartDisplayType];

        const valueFormatter = formatter;
        const colors = new ColorStack(CHART_COLORS.chartMainColors);

        const filters = this.resolvedWidgetFilters;
        const durationForApi = this.durationObject.forAPI;
        const apiGranularity = resolveMarketSharesApiTimeGranularity(
            filters,
            durationForApi.isWindow,
            MARKET_SHARES_GRAPH_METRIC_TYPE,
        );

        const { config, chartData } = getAreaChartConfig(
            parsedGraphData,
            colors,
            chartDisplayType,
            this.durationObject.forWidget,
            formatter,
            valueFormatter,
            apiGranularity,
        );

        this.chartConfig = {
            options: config,
            series: chartData,
        };

        this.legendItems = createLegendsItems(this.chartConfig.series);

        this.reactLegendComponent = LegendItems;

        this.widgetState = WidgetState.LOADED;
    }

    async getData() {
        const state = this._reduxService.getState();
        let isEnabled = true;

        if (this.id) {
            const segmentWidgetAvailability = state.segmentsModule?.segmentWidgetAvailability?.[this.id];

            if (segmentWidgetAvailability) {
                isEnabled = segmentWidgetAvailability?.isEnabled;
            }
        }

        if (!isEnabled) {
            this.widgetState = WidgetState.SOFT_LIMIT_REACHED;

            return;
        }

        try {
            const response = await this.getDataMetric();
            this.originalData = response;

            if (response) {
                this.initChart(response);
            } else {
                this.widgetState = WidgetState.ERROR;
            }

            this.notifyWidgetUpdated();
        } catch (error) {
            Sentry.errorTracker({
                message: "MarketSharesGraphWidget: Error",
                e: error,
                level: "error",
                params: {
                    extra: {
                        metric: this.metric,
                    },
                },
            });
            this.widgetState = WidgetState.ERROR;
        }
    }

    public getProUrl() {
        const { webSource, country, duration, filters } = this._widgetConfig?.properties;
        const industry = getIndustrieById(filters.category || this.apiParams.category);
        const { state, component } = this._metricConfig;
        const chartDisplayType = this.resolvedChartDisplayTypeForUrl;
        const durationForApi = DurationService.getDurationData(
            duration,
            this._widgetConfig.properties.comparedDuration,
            component,
        ).forAPI;

        const result = {
            duration,
            country,
            webSource,
            category: industry?.forUrl,
            marketSharesDisplayType: chartDisplayType,
            marketSharesChartType: "OVERTIME",
            marketSharesTimeGranularity: toTimeGranularitySymbol(
                resolveMarketSharesApiTimeGranularity(
                    filters,
                    durationForApi.isWindow,
                    MARKET_SHARES_GRAPH_METRIC_TYPE,
                ),
            ),
        };

        if (typeof state === "undefined") {
            return this._swNavigator.href("digitalsuite_webmarketanalysis_home", {}, {});
        }
        const url = this._swNavigator.href(state, result, {});

        return url;
    }
}

MarketSharesGraphWidget.register();
